import type { JourneyPage } from './types'

export const referralFlywheel: JourneyPage = {
  slug: 'referral-flywheel',
  intro: [
    { text: 'The MiFID II ', highlight: 'cost disclosure arrives' },
    { text: 'Your client sees what they ', highlight: 'paid. Again' },
    { text: 'But this time, they also see what they ', highlight: 'got' },
    { text: 'Tax savings. Performance. ', highlight: 'Peace of mind' },
    { text: 'This is ', highlight: 'Price to Value' },
  ],
  colorClass: 'referral',
  seo: {
    title: 'WealthSpace Referral Flywheel | Grow Through Referrals',
    description:
      'Turn happy clients into your best referral source. Automate adviser referral programmes and track client acquisition with WealthSpace.',
    keywords:
      'client referrals, adviser referral programme, client acquisition, referral tracking, financial adviser marketing, client growth, referral automation',
  },
  hero: {
    eyebrow: 'Imagine your next annual review',
    title: 'Turn MiFID II costs into',
    titleAccent: 'value stories',
    subtitle:
      "Stop apologising for your fees. Show clients exactly what they get - in pounds, peace of mind, and life outcomes. Transform awkward cost disclosures into confident value conversations.",
    microcopy: 'Real adviser. Real value. Real confidence.',
  },
  journeySection: {
    label: 'The Story',
    title: 'From "what do I pay you for?" to',
    titleAccent: '"worth every penny"',
    subtitle: 'The MiFID II cost disclosure arrives. But this time, the client is delighted.',
  },
  timeline: [
    {
      time: 'The dreaded letter arrives',
      icon: 'document',
      iconColor: 'pink',
      title: 'MiFID II annual cost disclosure',
      meta: 'The moment most advisers dread',
      body: "Every year, regulators require you to tell clients exactly what they've paid. Most advisers are good at talking their clients through the disclosure - but they could still do without it. There's a better way.",
      previewLabel: 'The typical approach',
      previewText:
        'A PDF statement - Numbers without context. No explanation - What did they actually get? Client confusion - "I paid HOW much?"',
    },
    {
      time: 'WealthSpace calculates the value',
      icon: 'chart',
      iconColor: 'teal',
      title: 'Value of Advice Tracker',
      meta: 'Quantifying what you actually delivered',
      body: 'Track the value youve delivered throughout the year. Tax savings, pension optimisation, investment returns vs benchmark, protection claims supported. Rich outputs backed by independent research. Real pounds. Real evidence.',
      previewLabel: 'What it tracks',
      previewText:
        'Tax savings - ISA/pension contributions, CGT planning, IHT mitigation. Performance delta - Your portfolio vs if theyd done nothing. Behavioural coaching - Value of staying invested during volatility.',
    },
    {
      time: 'Beautiful value reports',
      icon: 'document',
      iconColor: 'amber',
      title: 'Client-ready collateral',
      meta: 'One-pagers, presentations, and value reports',
      body: 'Generate professional value reports that show the cost alongside the benefit. Clear charts. Simple language. A narrative that helps clients understand exactly what they got for their investment.',
      previewLabel: 'From the value report',
      previewText:
        '"This year, you paid £3,240 in advice fees. Your adviser delivered £18,400 in quantifiable value - a 5.7x return on your investment in advice."',
    },
    {
      time: 'MiFID II with confidence',
      icon: 'shield',
      iconColor: 'emerald',
      title: 'Statement + Value Report',
      meta: 'Compliance meets client experience',
      body: 'Send the MiFID II cost disclosure alongside your value report. The cost statement becomes a proud demonstration of value delivered, not an awkward obligation to fulfil.',
      previewLabel: 'What the client receives',
      previewText:
        'MiFID II cost disclosure - Fully compliant, clear formatting. Value of advice report - Side-by-side comparison. Personal video summary - You explaining their year.',
    },
    {
      time: 'The client response',
      icon: 'users',
      iconColor: 'violet',
      title: '"Worth every penny"',
      meta: 'The reaction that changes everything',
      body: 'Instead of uncomfortable questions about fees, clients thank you for the transparency. They understand the value. They feel confident recommending you to friends. The cost disclosure becomes a retention tool.',
      previewLabel: 'Client email, the next morning',
      previewText:
        '"Id never thought about it this way before. I always knew you were helping us, but seeing the actual numbers - were paying £3k and getting £18k back. Thats incredible. Thank you for being so transparent."',
    },
  ],
  result: {
    title: 'Fee confidence.',
    titleAccent: 'Client loyalty.',
    body: 'When clients understand the value you deliver, fee conversations become celebrations. Retention improves. Referrals increase. Your confidence soars.',
    stats: [
      { value: '5.7x', label: 'Average value vs cost' },
      { value: '98%', label: 'Client retention rate' },
      { value: '0', label: 'Fee complaints' },
    ],
  },
  features: {
    label: 'What powers the transformation',
    title: 'Tools that',
    titleAccent: 'prove your worth',
    subtitle:
      'Everything you need to demonstrate value, build confidence, and turn fee discussions into celebrations.',
    items: [
      {
        icon: 'chart',
        title: 'Value Tracker',
        body: 'Track and quantify tax savings, pension optimisation, investment performance, and behavioural coaching. Rich outputs backed by independent research.',
      },
      {
        icon: 'document',
        title: 'Value Reports',
        body: 'Beautiful, client-friendly documents that show cost alongside value. Charts, comparisons, and narratives that make the numbers meaningful.',
      },
      {
        icon: 'shield',
        title: 'MiFID II Integration',
        body: 'Compliant cost disclosures paired with value demonstrations. Turn a regulatory requirement into a client retention opportunity.',
      },
    ],
  },
  testimonial: {
    quote:
      'I used to dread sending out the MiFID II statements. Now I actually look forward to it. Clients email me back saying thank you. The value report changes everything - they see £3k in fees and £18k in value, and suddenly Im not defending my prices, Im being thanked for the transparency.',
    authorName: 'James Mitchell',
    authorRole: 'IFA, Mitchell Financial Planning',
    authorLocation: 'Manchester',
    initials: 'JM',
  },
  cta: {
    title: 'Prove your',
    titleAccent: 'value',
    subtitle:
      'Book a demo and see how WealthSpace helps you demonstrate the true value of financial advice.',
    note: 'Stop apologising for fees. Start celebrating value.',
  },
}
